import React, {Component, useContext} from 'react'
import moment from 'moment-timezone'
import axios from 'axios'
import map from 'lodash/map'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import TenantDatePicker from '../shared/TenantDatePicker'
import Modal from '../shared/modal'
import PlusMinusWrapper from '../shared/plusMinusWrapper'
import { ProductContainer, ContainerSlot, ProductWithCategory, Product } from './types'
import { LocalizationContext, LocalizationContextType } from '../shared/contexts'
import I18n, { t } from '../../i18n'
import Icon from '../shared/icon'
import SubmitButton from '../shared/SubmitButton'
import SelectProductDropdown from './SelectProductDropdown'

interface Props {
  expandedContainerSlot: ContainerSlot,
  expandedContainer: ProductContainer,
  closeContainer: React.MouseEventHandler,
  refreshStock: Function,
  canRemove: boolean,
  canViewDosingLogs: boolean,
}

interface State {
  errors: string[],
  currentView: string,
  replacerContainer: ProductContainer,
  expandedContainer: ProductContainer,
  productOptions: ProductWithCategory[],
  dataLoading: boolean,
  recentlyUsedOnly: boolean
}

const scope = 'stock_control.product_container'

class ProductContainerModal extends Component<Props, State> {
  static contextType = LocalizationContext

  blankContainer: ProductContainer
  weightUnit: string
  loadProductOptionsRequestId: number = 0

  constructor(props: Props) {
    super(props)
    moment.locale(I18n.locale)

    this.blankContainer = {
      slot_number: props.expandedContainerSlot.number,
      liquid: props.expandedContainerSlot.isLiquid,
      product: props.expandedContainer ? props.expandedContainer.product : null,
      expiration_date: null,
      initial_amount: 0,
      notes: '',
      product_container_warnings: {}
    }

    this.state = {
      errors: [],
      currentView: props.expandedContainer ? 'show' : 'edit',
      replacerContainer: this.blankContainer,
      expandedContainer: this.blankContainer,
      productOptions: [],
      dataLoading: true,
      recentlyUsedOnly: true
    }
  }

  componentDidMount() {
    this.loadProductWithCategory()
    this.loadProductOptions()
  }

  loadProductWithCategory = () => {
    const productId = this.state.replacerContainer.product?.id

    if(productId) {
      axios.get(`/stocks/product?product_id=${productId}`).then(({ data }) => {
        this.setState({
          expandedContainer: {
            ...this.props.expandedContainer,
            product: data
          },
          dataLoading: false
        })
      })
    } else {
      this.setState({ dataLoading: false })
    }
  }

  loadProductOptions = () => {
    const type = this.props.expandedContainerSlot.isLiquid ? 'liquid_only' : 'solid_only'
    const recentlyUsedParam = this.state.recentlyUsedOnly ? '&recently_used=true' : ''
    const requestId = ++this.loadProductOptionsRequestId

    axios.get(`/products/products_with_categories?${type}=true${recentlyUsedParam}`).then(({ data }) => {
      if (requestId === this.loadProductOptionsRequestId) {
        this.setState({ productOptions: data })
      }
    })
  }

  handleRecentlyUsedToggle = (checked: boolean) => {
    this.setState({ recentlyUsedOnly: checked }, this.loadProductOptions)
  }

  // Keeps the currently selected product choosable/visible in the dropdown even when the
  // recently-used filter would otherwise exclude it, so replacing a container never hides its own
  // existing product from the list it's shown in.
  productOptionsWithCurrentSelection = (currentProduct: Product | null, productOptions: ProductWithCategory[]): ProductWithCategory[] => {
    if (!currentProduct || productOptions.some(option => option.id === currentProduct.id)) {
      return productOptions
    }

    return [...productOptions, {
      id: currentProduct.id,
      name: currentProduct.name,
      description: '',
      product_category_id: currentProduct.product_category?.id ?? 0,
      product_category_name: currentProduct.product_category?.name ?? '',
      image_url_small: currentProduct.image_url_medium,
      image_url_medium: currentProduct.image_url_medium
    }]
  }

  changeView = (value) => {
    this.setState({ currentView: value })
  }

  handleFormCancel = () => {
    const { expandedContainer } = this.state

    if (expandedContainer.product) {
      this.changeView('show')
      this.resetReplacerContainerForm()
    } else {
      this.props.closeContainer(null)
    }
  }

  handleReplacerContainerChange = (name, value) => {
    let replacerContainer: ProductContainer = { ...this.state.replacerContainer }

    replacerContainer[name] = value

    this.setState({ replacerContainer })
  }

  handleReplacerContainerIntChange = (name, passedValue) => {
    const value = passedValue ? parseFloat(passedValue) : 0
    if (isNaN(value)) return

    this.handleReplacerContainerChange(name, Math.max(0, value))
  }

  resetReplacerContainerForm = () => {
    this.setState({ replacerContainer: { ...this.blankContainer } })
  }

  createProductContainer = () => {
    const { slot_number, liquid, product, expiration_date, initial_amount, notes } = this.state.replacerContainer
    // expiration_date column is a `date` (calendar day, no zone). Submit
    // YYYY-MM-DD so AR stores the exact day the user picked — sending a UTC
    // ISO timestamp would let AR's Date.parse silently shift it for tenants
    // east of UTC (Tokyo midnight = UTC previous afternoon → stored as
    // previous day).
    const expirationDateStr = expiration_date ? moment(expiration_date).format('YYYY-MM-DD') : null
    return axios.post('/product_containers?format=json', { product_container: {
      slot_number, liquid, expiration_date: expirationDateStr, initial_amount, notes, product_id: product.id
    }}).then(() => {
      this.props.refreshStock()
      this.changeView('saveResult')
    }).catch(error => {
      const errors = error.response.data.full_errors
      errors && this.setState({ errors: Object.values(errors) })
      this.changeView('saveError')
    })
  }

  removeProductContainer = () => {
    const { expandedContainer } = this.state

    return axios.put(`/product_containers/${expandedContainer.id}/remove`).then(({ data }) => {
      this.props.refreshStock()
      this.changeView('removeResult')
    }).catch(({ response }) => {
      this.changeView('saveError')
    })
  }

  openEdit = (containerForEdit) => {
    this.setState({replacerContainer: {...containerForEdit}})
    this.changeView('edit')
  }

  onModalClose = (e) => {
    this.props.closeContainer(e)
    this.resetReplacerContainerForm()
  }

  modalShowContent = () => {
    const expandedContainer = this.state.expandedContainer

    const { date_format: dateFormat } = this.context as LocalizationContextType

    const expirationDate = moment(expandedContainer.expiration_date)
    const currentDate = moment().startOf('day')
    const daysUntilExpiration = expirationDate.diff(currentDate, 'days')

    const currentAmount = expandedContainer.initial_amount - expandedContainer.used_amount
    const amountPercent = (currentAmount / expandedContainer.initial_amount) * 100

    let amountStatus: string
    if (amountPercent <= 20) {
      amountStatus = 'danger'
    } else if (amountPercent <= 50)
      amountStatus = 'warning'
    else {
      amountStatus = 'success'
    }

    let expirationStatus: string
    if (daysUntilExpiration <= 2) {
      expirationStatus = 'danger'
    } else if (daysUntilExpiration <= 7) {
      expirationStatus = 'warning'
    } else {
      expirationStatus = 'success'
    }

    return (
      <div className='container-modal show'>
        <div className='product-info'>
          <div className="product-image">
            <img className='product-image' src={expandedContainer.product.image_url_medium} />
          </div>
          <div className="product-content">
            <span className='product-ancestry'>{[t('categories', { scope }), expandedContainer.product.ancestry_tree].filter(Boolean).join(' / ')}</span>
            <span className='product-name'>{expandedContainer.product.name}</span>
          </div>
        </div>
        <div className='field'>
          <span className='field-name'>{t('amount', { scope })}</span>
          <span className='field-value'>
            {/* round up to 3 decimal places, no decimal places if not needed */}
            <span className={`${amountStatus}`}>{ Math.round(currentAmount*1000)/1000 }</span>
            <span> / {`${expandedContainer.initial_amount} ${this.weightUnit}`}</span>
          </span>
        </div>
        <div className='field'>
          <span className='field-name'>{t('expiry_date', { scope })}</span>
          <span className='field-value'>
            <span className={expirationStatus}>{expirationDate.format(dateFormat)}</span>
          </span>
        </div>
        <div className='field-text'>
          <div className='field-name'>{t('notes', { scope })}</div>
          <div className='field-value'>{expandedContainer.notes}</div>
        </div>
        <div className='buttons'>
          <button className='btn btn-primary' onClick={() => this.openEdit(expandedContainer)}>{t('replace_container', { scope } )}</button>
          {this.dosingLogsLink()}
        </div>
      </div>
    )
  }

  modalEditContent = () => {
    const { replacerContainer, expandedContainer, productOptions } = this.state
    const { product } = replacerContainer
    const { date_format: dateFormat } = this.context as LocalizationContextType

    return (
      <div className='container-modal edit'>
        <div className="attribute-wrapper">
          <span className="input-label">{t('product', { scope })}</span>
          <div className='input-wrapper select-product-dropdown'>
              <Autocomplete
                options={this.productOptionsWithCurrentSelection(product, productOptions).sort((a, b) => -(b.product_category_name || '').localeCompare(a.product_category_name || ''))}
                groupBy={product => product.product_category_name || 'Uncategorized'}
                sx={{ width: '100%' }}
                getOptionLabel={product => product.name}
                value={this.state.replacerContainer.product as any}
                isOptionEqualToValue={(opt, value) => opt.id === value.id}
                onChange={(_e, product) => this.handleReplacerContainerChange('product', product)}
                renderInput={(params) => <TextField {...params}/>}
              />
          </div>
        </div>
        <div className="attribute-wrapper recently-used-filter">
          <span className="input-label" />
          <FormControlLabel
            control={
              <Checkbox
                checked={this.state.recentlyUsedOnly}
                onChange={(_e, checked) => this.handleRecentlyUsedToggle(checked)}
              />
            }
            label={t('recently_used_only', { scope })}
          />
        </div>
        <div className='product-info'>
          <div className="product-image">
            <img className='product-image' src={product ? product.image_url_medium : 'images/product-placeholder.svg'} />
          </div>
          <div className="product-content">
            {product && <span className='product-ancestry'>{[t('categories', { scope }), product.ancestry_tree].filter(Boolean).join(' / ')}</span>}
            <span className='product-name'>{product ? product.name : 'New product' }</span>
          </div>
        </div>
        <div className="attribute-wrapper narrow">
          <span className='input-label'>{t('amount', { scope })}</span>
          <PlusMinusWrapper
            step={1}
            value={Number(replacerContainer.initial_amount)}
            onChange={(_, value) => this.handleReplacerContainerIntChange('initial_amount', value)}
          >
            <div className='subwrapper'>
              <input
                type='number'
                className={this.weightUnit === 'kg' ? 'with-unit-offset-oz-kg' : 'with-unit-offset-lbs'}
                value={Number(replacerContainer.initial_amount)}
                maxLength={4}
                onChange={({ target: { value } }) => this.handleReplacerContainerIntChange('initial_amount', value)}
              />
              <span className='input-unit'> {this.weightUnit}</span>
            </div>
          </PlusMinusWrapper>
        </div>
        <div className="attribute-wrapper">
          <span className='input-label'>{t('expiry_date', { scope })}</span>
          <div className='input-wrapper date-wrapper'>
            <TenantDatePicker
              format={dateFormat}
              value={replacerContainer.expiration_date ? moment(replacerContainer.expiration_date) : null}
              onChange={date => this.handleReplacerContainerChange('expiration_date', date)}
            />
          </div>
        </div>
        <div className='textarea-wrapper attribute-wrapper'>
          <span className='input-label'>{t('notes', { scope })}</span>
          <textarea className='input-control' rows={4}
            value={replacerContainer.notes}
            onChange={({ target: { value } }) => this.handleReplacerContainerChange('notes', value)}
          />
        </div>
        <div className='buttons'>
          {expandedContainer && expandedContainer.id && this.props.canRemove &&
            <button className='btn btn-danger' onClick={() => this.changeView('removePrompt')}>{t('remove_container', { scope })}</button>
          }
          <button className='btn btn-secondary' onClick={this.handleFormCancel}>{t('cancel')}</button>
          <button className='btn btn-primary'
            onClick={() => this.changeView('savePrompt')}
            disabled={!this.isReplacerContainerValid()}
          >
            {t('replace_container', { scope } )}
          </button>
          {this.dosingLogsLink()}
        </div>
      </div>
    )
  }

  productImage = (container) => {
    const { date_format: dateFormat } = this.context as LocalizationContextType

    if(container.product) {
      return (
        <div className='product-before'>
          <div className='product-image'><img src={container.product.image_url_medium}/></div>
          <div className='heavy-text'>{container.product.name}</div>
          <div className='product-expiration-date'>{moment(container.expiration_date).format(dateFormat)}</div>
        </div>
      )
    }
    return (
      <div className='product-before empty'>
        <div className='product-image'><Icon name='emptyProduct'/></div>
        <div className='heavy-text'>{t('empty', { scope })}</div>
      </div>
    )
  }

  modalSavePrompt = () => {
    const { expandedContainer, replacerContainer } = this.state
    const { date_format: dateFormat } = this.context as LocalizationContextType

    return (
      <div className='container-modal save-prompt'>
        <div className='title-icon'>
          <Icon name='replace'/>
        </div>
        <div className='heavy-text'>{t('replace_container', { scope })}</div>
        <div className='product-before-after'>
          {this.productImage(expandedContainer)}
          <div className='product-after'>
            <div className='product-image'><img src={replacerContainer.product.image_url_medium}/></div>
            <div className='heavy-text'>{replacerContainer.product.name}</div>
            <div className='product-expiration-date'>{moment(replacerContainer.expiration_date).format(dateFormat)}</div>
          </div>
        </div>
        <p className='confirmation-prompt'>
          {t('line1', { scope: 'confirmation' })}<br/>
          {t('line2', { scope: 'confirmation' })}
        </p>
        <div className='buttons'>
          <button className='btn btn-secondary' onClick={() => this.changeView('edit')}>{t('cancel')}</button>
          <SubmitButton className='btn btn-primary' onClick={this.createProductContainer}>{t('proceed')}</SubmitButton>
        </div>
      </div>
    )
  }

  modalRemovePrompt = () => {
    const { expandedContainer } = this.state
    const { date_format: dateFormat } = this.context as LocalizationContextType

    return (
      <div className='container-modal save-prompt'>
        <div className='title-icon danger'>
          <i className='fa fa-exclamation-circle' />
        </div>
        <div className='heavy-text'>{t('remove_container', { scope })}</div>
        <div className='product-before-after'>
          {expandedContainer &&
            <div className='product-after'>
              <div className='product-image'><img src={expandedContainer.product.image_url_medium}/></div>
              <div className='heavy-text'>{expandedContainer.product.name}</div>
              <div className='product-expiration-date'>{moment(expandedContainer.expiration_date).format(dateFormat)}</div>
            </div>
          }
        </div>
        <p className='confirmation-prompt'>
          {t('line1', { scope: 'confirmation' })}<br />
          {t('line2', { scope: 'confirmation' })}
        </p>
        <div className='buttons'>
          <button className='btn btn-secondary' onClick={() => this.changeView('edit')}>{t('cancel')}</button>
          <SubmitButton className='btn btn-primary' onClick={this.removeProductContainer}>{t('proceed')}</SubmitButton>
        </div>
      </div>
    )
  }

  modalSaveResult = () => {
    const { replacerContainer } = this.state
    const { date_format: dateFormat } = this.context as LocalizationContextType

    return (
      <div className='container-modal save-result'>
        <div className='title-icon success'>
          <i className='fa fa-check-circle' />
        </div>
        <div className='heavy-text'>{t('save_modal.title', { scope })}</div>
        <div className='product-before-after'>
          <div className='product-after'>
            <div className='product-image'><img src={replacerContainer.product.image_url_medium}/></div>
            <div className='heavy-text'>{replacerContainer.product.name}</div>
            <div className='product-expiration-date'>{moment(replacerContainer.expiration_date).format(dateFormat)}</div>
          </div>
        </div>
        <p className='confirmation-prompt'>
          {t('save_modal.text', { scope })}
        </p>
        <div className='buttons'>
          <button className='btn btn-primary' onClick={this.onModalClose}>{t('continue')}</button>
        </div>
      </div>
    )
  }

  modalRemoveResult = () => {
    return (
      <div className='container-modal save-result'>
        <div className='title-icon success'>
          <i className='fa fa-check-circle' />
        </div>
        <div className='heavy-text'>{t('remove_modal.title', { scope })}</div>
        <p className='confirmation-prompt mt-5'>
          {t('remove_modal.text', { scope })}
        </p>
        <div className='buttons'>
          <button className='btn btn-primary' onClick={this.props.closeContainer}>{t('continue')}</button>
        </div>
      </div>
    )
  }

  modalSaveError = () => {
    const { errors } = this.state

    return (
      <div className='container-modal save-result'>
        <div className='title-icon danger mt-5'>
          <i className='fa fa-times-circle' />
        </div>
        {map(errors, error =>
          <div className='heavy-text mt-1 mb-5'>{error}</div>
        )}
        {errors.length === 0 && <div className='heavy-text mt-1 mb-5'>{t('title', { scope: 'error_modal' })}</div>}
        <div className='buttons'>
          <button className='btn btn-primary' onClick={() => this.changeView('edit')}>{t('button', { scope: 'error_modal' })}</button>
        </div>
      </div>
    )
  }

  dosingLogsLink = () => {
    if (!this.props.canViewDosingLogs) return null

    const { number, isLiquid } = this.props.expandedContainerSlot
    const feederSlot = `${isLiquid ? 'B' : 'A'}${number}`
    const url = `/admin/command_step_durations?view=dosing_history&feeder_slot=${feederSlot}`

    return (
      <a className='dosing-logs-link' href={url} target='_blank' rel='noopener noreferrer'>
        {t('go_to_dosing_logs', { scope })}
        <i className='fa fa-chevron-right ml-2' />
      </a>
    )
  }

  isReplacerContainerValid = () => {
    const { replacerContainer: { expiration_date, initial_amount, product } } = this.state

    return product && product.id && initial_amount && expiration_date
  }

  headerText = () => {
    const { currentView } = this.state
    const slotNumber = this.state.replacerContainer.slot_number
    switch (currentView) {
      case 'show': return t('product_container', { scope: 'replenishment.replenishment_modal', slotNumber })
      case 'edit': return t('replace_container', { scope: 'replenishment.replenishment_modal', slotNumber })
      default: return null
    }
  }

  render() {
    const { currentView } = this.state

    const headerText = this.headerText()
    const { weight_scale } = this.context as LocalizationContextType

    this.weightUnit = {
      'metric': 'kg',
      'imperial': 'lbs'
    }[weight_scale]

      return (
        <>
          {!this.state.dataLoading &&
            <Modal onClose={this.props.closeContainer} widen='true'
              headerContent={headerText && <h3 className='header'>{headerText}</h3>}
            >
              {currentView === 'show' && this.modalShowContent()}
              {currentView === 'edit' && this.modalEditContent()}
              {currentView === 'savePrompt' && this.modalSavePrompt()}
              {currentView === 'saveResult' && this.props.expandedContainer && this.modalSaveResult()}
              {currentView === 'removePrompt' && this.modalRemovePrompt()}
              {currentView === 'removeResult' && this.modalRemoveResult()}
              {currentView === 'saveError' && this.modalSaveError()}
            </Modal>
          }
        </>
      )
    }
}

export default ProductContainerModal
